// src/annihilation/useAnnihilationProfile.js
// ============================================================
//  ANNIHILATION — the live profile hook.  (perf pass v2)
//
//  v1 listened to the WHOLE shared IE profile (profiles/{uid}).
//  That node carries the entire Terror collection, achievements,
//  and cosmetics — so every Blood Money / XP / material delta
//  (two runTransactions per kill!) delivered a full snapshot and
//  snap.val() deserialized ALL of it synchronously on the main
//  thread. In the field that presented as a ~1s freeze while the
//  sim kept ticking, then a catch-up teleport. Classic.
//
//  v2 listens ONLY to the three things Annihilation reads:
//    · profiles/{uid}/annihilation___bloodMoney
//    · profiles/{uid}/annihilation___character
//    · profiles/{uid}/divineCrests          (READ-ONLY here. Always.)
//  Snapshots are now bytes, not the Empire. The write surface
//  stays locked to profileWrites.js — nothing about writes moves.
//
//  Contract note: `profile` is still returned for compatibility
//  but is now a MINIMAL LANE VIEW ({ uid, lane fields, crests }),
//  not the full IE profile. Nothing in the codebase read beyond
//  the lane fields (audited Jul 21 26); any future screen that
//  needs the wide profile should attach its own narrow listener
//  to the node it actually wants — never the root.
//
//  Character identity law: a snapshot that decodes to the SAME
//  character JSON hands back the SAME object, so unrelated writes
//  can never reconcile the 3D scene through a fresh identity.
//
//  On first attach it lazily ensures the Blood Money node exists
//  (idempotent — an existing balance is never touched).
// ============================================================

import { useEffect, useMemo, useRef, useState } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "./firebase";
import { LANE, ensureBloodMoney } from "./profileWrites";

/**
 * @param {string|null} uid  Pass null/undefined before login —
 *                           the hook idles safely.
 * @returns {{
 *   profile: object|null,        // minimal lane view (see note above)
 *   bloodMoney: number,          // annihilation___bloodMoney (0 if unset)
 *   character: object|null,      // annihilation___character
 *   divineCrests: number,        // READ-ONLY here. Always.
 *   loading: boolean,
 * }}
 */
export function useAnnihilationProfile(uid) {
  const [bloodMoney, setBloodMoney] = useState(0);
  const [character, setCharacter] = useState(null);
  const [divineCrests, setDivineCrests] = useState(0);
  const [loading, setLoading] = useState(!!uid);
  const ensuredRef = useRef(null);
  const charJsonRef = useRef(null); // identity guard

  useEffect(() => {
    if (!uid) {
      setBloodMoney(0);
      setCharacter(null);
      setDivineCrests(0);
      charJsonRef.current = null;
      setLoading(false);
      return;
    }
    setLoading(true);
    charJsonRef.current = null;

    // Lazy lane init — once per uid per mount.
    if (ensuredRef.current !== uid) {
      ensuredRef.current = uid;
      ensureBloodMoney(uid).catch((e) =>
        console.warn("[annihilation] bloodMoney init failed", e)
      );
    }

    const base = `profiles/${uid}`;

    const unsubBm = onValue(ref(db, `${base}/${LANE}bloodMoney`), (snap) => {
      const v = snap.val();
      setBloodMoney(typeof v === "number" ? v : 0);
    });

    const unsubChar = onValue(ref(db, `${base}/${LANE}character`), (snap) => {
      const v = snap.exists() ? snap.val() : null;
      const json = v ? JSON.stringify(v) : null;
      // identity law: same content → same object, no re-render.
      if (json !== charJsonRef.current) {
        charJsonRef.current = json;
        setCharacter(v);
      }
      setLoading(false); // character is the gate App.js waits on
    });

    const unsubCrests = onValue(ref(db, `${base}/divineCrests`), (snap) => {
      const v = snap.val();
      setDivineCrests(typeof v === "number" ? v : 0);
    });

    return () => {
      unsubBm();
      unsubChar();
      unsubCrests();
    };
  }, [uid]);

  // Minimal lane view — keeps the v1 return shape alive without
  // hauling the Empire through the wire. Memo'd so consumers that
  // only destructure lane fields never see churn.
  const profile = useMemo(() => {
    if (!uid) return null;
    return {
      uid,
      [`${LANE}bloodMoney`]: bloodMoney,
      [`${LANE}character`]: character,
      divineCrests,
    };
  }, [uid, bloodMoney, character, divineCrests]);

  return { profile, bloodMoney, character, divineCrests, loading };
}
